import {
  Autocomplete,
  Box,
  BoxStatus,
  Button,
  Flexbox,
  Label,
  OpePanel,
  Stack,
  StackRow,
  DateRangeInput,
  GraphicFilters,
} from "codekit";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Controller, useForm, useWatch } from "react-hook-form";
import { getDataUser } from "../../../../libs/redux/slices/authSlice/authSlice";
import { useActionsGraphRangetFilters } from "../../../../utils/hooks/graphs/useActionsGraphRangetFilters";
import {
  useGetOpePanelDataQuery,
  useGetOpePanelDoDataMutation,
  useGetDoGraphicByIdMutation,
} from "../../../../libs/redux/slices/Graficsx/Opepanel/opepanelApiSlice";
import { useGetUrlRedirectCimaMutation } from "../../../../libs/redux/slices/Graficsx/graficsxApiSlice";
import { useGetClientGraphicByIdMutation } from "../../../../libs/redux/slices/Reportxx/Repfinac/repfinacApiSlice";

/**
 * Grafica Panel de operaciones aduaneras
 * @author devpremier
 * @since 2026-04-21
 */

const ESTADOS = ["pendiente", "accion", "espera", "lista"];
const PER_PAGE = 50;

const Opepanel = ({ graphName }) => {
  const { USRNOMXX } = useSelector(getDataUser);

  const { control, reset } = useForm({
    mode: "onBlur",
    defaultValues: { CLIIDXXX: "", DOCNROXX: "" },
  });
  const values = useWatch({ control });

  // Estado rango de fecha
  const [valueDateRange, hookHandleRange, clearDateRange] = useActionsGraphRangetFilters();

  /**
   * Adapta el output de DateRangeInput (Date objects) al formato DD/MM/YYYY que espera el hook
   * @param {{ option: { startDate: Date, endDate: Date } }} context
   */
  const changeRange = (context) => {
    const { startDate, endDate } = context.option;
    const fmt = (d) => {
      if (!d) return "";
      const date = new Date(d);
      const day = String(date.getDate()).padStart(2, "0");
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const y = date.getFullYear();
      return `${day}/${m}/${y}`;
    };
    hookHandleRange({ option: [fmt(startDate), fmt(endDate)] });
  };

  // Autocomplete cliente
  const [resultsClient, setResultsClient] = useState([]);
  const [getClientById] = useGetClientGraphicByIdMutation();

  // Autocomplete DO
  const [resultsDo, setResultsDo] = useState([]);
  const [getDoById] = useGetDoGraphicByIdMutation();

  /**
   * Construye los params de filtro a enviar a la query
   * @returns {object}
   */
  const buildFilters = useCallback(() => {
    const filters = {};
    if (values.CLIIDXXX) {
      filters["filters[MDOSYYYY.CLIIDXXX]"] = values.CLIIDXXX.split(" - ")[0];
    }
    if (values.DOCNROXX) {
      filters["filters[MDOSYYYY.DOCNROXX]"] = values.DOCNROXX.split(" - ")[0];
    }
    if (valueDateRange[0]) {
      filters["filters[MDOSYYYY.REGFECXX][FROM]"] = valueDateRange[0];
    }
    if (valueDateRange[1]) {
      filters["filters[MDOSYYYY.REGFECXX][TO]"] = valueDateRange[1];
    }
    return filters;
  }, [values, valueDateRange]);

  const [appliedFilters, setAppliedFilters] = useState({});

  /**
   * Aplica los filtros actuales y dispara la petición al API
   */
  const handleApplyFilters = () => {
    setAppliedFilters(buildFilters());
  };

  const { data: response, isLoading } = useGetOpePanelDataQuery(appliedFilters, {
    refetchOnMountOrArgChange: true,
  });
  const opePanelData = response?.data;

  const [doData, setDoData] = useState({});
  const [doPagination, setDoPagination] = useState({});
  const [fetchDoData] = useGetOpePanelDoDataMutation();
  const [getUrlRedirectCima] = useGetUrlRedirectCimaMutation();

  /**
   * Busca clientes perfilados para el autocomplete
   * @param {string} CLIIDXXX
   * @param {Function} cb
   */
  const searchClient = async (CLIIDXXX, cb) => {
    return await getClientById({ filters: { CLIIDXXX } })
      .unwrap()
      .then((res) => setResultsClient(res.data))
      .catch(() => {})
      .finally(() => cb());
  };

  /**
   * Busca DOs perfilados para el autocomplete
   * @param {string} DOCNROXX
   * @param {Function} cb
   */
  const searchDo = async (DOCNROXX, cb) => {
    return await getDoById({ filters: { DOCNROXX } })
      .unwrap()
      .then((res) => setResultsDo(res.data))
      .catch(() => {})
      .finally(() => cb());
  };

  const fetchAndStore = (tab, estado, page, append) => {
    const faseKey = tab.toLowerCase();
    const ids = opePanelData?.doIds?.[faseKey]?.[estado] ?? [];
    if (ids.length === 0) return;

    setDoPagination((prev) => ({
      ...prev,
      [tab]: {
        ...(prev[tab] ?? {}),
        [estado]: { ...(prev[tab]?.[estado] ?? { page: 1, hasMore: false }), isLoading: true },
      },
    }));

    fetchDoData({ ids, estado, page, perPage: PER_PAGE })
      .unwrap()
      .then((res) => {
        const currentPage = res.meta?.page;
        const hasMore = res.meta ? currentPage < (res.meta.last_page ?? page) : (res.data?.length ?? 0) >= PER_PAGE;
        setDoData((prev) => {
          const prevTab = prev[tab] ?? {};
          return {
            ...prev,
            [tab]: {
              ...prevTab,
              [estado]: append ? [...(prevTab[estado] ?? []), ...res.data] : res.data,
            },
          };
        });

        setDoPagination((prev) => ({
          ...prev,
          [tab]: {
            ...(prev[tab] ?? {}),
            [estado]: { page: currentPage, hasMore, isLoading: false },
          },
        }));
      })
      .catch(() => {
        setDoPagination((prev) => ({
          ...prev,
          [tab]: {
            ...(prev[tab] ?? {}),
            [estado]: { ...(prev[tab]?.[estado] ?? { page, hasMore: false }), isLoading: false },
          },
        }));
      });
  };

  useEffect(() => {
    if (!opePanelData?.tabs?.length || !opePanelData?.doIds) return;
    setDoData({});
    setDoPagination({});
    opePanelData.tabs.forEach((tab) => {
      ESTADOS.forEach((estado) => {
        fetchAndStore(tab, estado, 1, false);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opePanelData]);

  const handleLoadMore = ({ tab, statKey }) => {
    const currentPage = doPagination[tab]?.[statKey]?.page ?? 1;
    fetchAndStore(tab, statKey, currentPage + 1, true);
  };

  // Redirige al tracking usando TODOS los IDs del bucket (no solo los cargados)
  const handleClickChart = async ({ tab, statKey }) => {
    const faseKey = tab.toLowerCase();
    const allIds = opePanelData?.doIds?.[faseKey]?.[statKey] ?? [];
    if (allIds.length === 0) return;
    const payload = {
      MODIDXXX: "117",
      PROIDXXX: "100",
      [`filters[0][MDOSYYYY.DOCNROXX][IN]`]: allIds,
    };
    await getUrlRedirectCima(payload)
      .unwrap()
      .then((res) => window.open(res.data, "_blank"))
      .catch();
  };

  // Redirige al tracking para una sola operación
  const handleClickDO = async ({ id }) => {
    const payload = {
      MODIDXXX: "117",
      PROIDXXX: "100",
      [`filters[0][MDOSYYYY.DOCNROXX][IN]`]: [id],
    };
    await getUrlRedirectCima(payload)
      .unwrap()
      .then((res) => window.open(res.data, "_blank"))
      .catch();
  };

  const handleResetFilters = () => {
    reset();
    clearDateRange();
    setAppliedFilters({});
  };

  if (isLoading) {
    return (
      <Box spacing={5}>
        <BoxStatus variant="loading" text="Cargando información..." />
      </Box>
    );
  }

  if (!opePanelData) {
    return null;
  }

  return (
    <Box spacing={5}>
      <GraphicFilters
        containerName={graphName}
        graphicKeyName="opepanel"
        clearFilters={() => handleResetFilters()}
        onClickVerMas={() => handleResetFilters()}
      >
        <Box mb={8} borderBottom={1} spacing={2}>
          <Stack gap={3} columnsVariant="form-3">
            <StackRow>
              <Label>Cliente</Label>
              <Controller
                control={control}
                name="CLIIDXXX"
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    placeholder="Buscar..."
                    textLoading="Buscando..."
                    textNoResults="No se encontraron registros"
                    minLengthToSearch={2}
                    selected={value}
                    searchResults={resultsClient}
                    onChangeSearch={(val, cb) => searchClient(val, cb)}
                    onItemClick={(ctx) => onChange(`${ctx.item.label}`)}
                    onResetSelected={() => onChange("")}
                  />
                )}
              />
            </StackRow>
            <StackRow>
              <Label>Número de DO</Label>
              <Controller
                control={control}
                name="DOCNROXX"
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    placeholder="Buscar..."
                    textLoading="Buscando..."
                    textNoResults="No se encontraron registros"
                    minLengthToSearch={2}
                    selected={value}
                    searchResults={resultsDo}
                    onChangeSearch={(val, cb) => searchDo(val, cb)}
                    onItemClick={(ctx) => onChange(`${ctx.item.label}`)}
                    onResetSelected={() => onChange("")}
                  />
                )}
              />
            </StackRow>
            <StackRow>
              <Label>Fecha de Creación DO</Label>
              <DateRangeInput valueFilterDate={valueDateRange} onChangeValueFilterDate={(ctx) => changeRange(ctx)} />
            </StackRow>
          </Stack>
          <Box mt={4}>
            <Flexbox justifyContent="end">
              <Button type="button" variant="primary-bold" size="s" onClick={handleApplyFilters}>
                Aplicar filtros
              </Button>
            </Flexbox>
          </Box>
        </Box>
        <OpePanel
          userName={USRNOMXX}
          chartData={opePanelData.chartData}
          tabs={opePanelData.tabs}
          tabStats={opePanelData.tabStats}
          doData={doData}
          doPagination={doPagination}
          onClickTracking={handleClickChart}
          onClickDo={handleClickDO}
          onLoadMore={handleLoadMore}
        />
      </GraphicFilters>
    </Box>
  );
};

export default Opepanel;
